import React from 'react'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import {Link, withRouter} from 'react-router-dom'
import injectSheet from 'react-jss'
import classNames from 'classnames'
import uniq from 'lodash-es/uniq'
import isEqual from 'lodash-es/isEqual'
import routesHelper, {goalNewPath} from '@prepler/core/lib/routesHelper'
import needLoadRunner from '@prepler/core/lib/utils/needLoadRunner'
import {denormalizeUniversity} from '@prepler/core/lib/schemas'
import * as goalActions from '@prepler/core/lib/actions/learning/goalActions'
import {loadUniversitiesByIds} from '@prepler/core/lib/actions/universityActions'
import {goalsSelector} from '@prepler/core/lib/reducers/learning/goalsReducer'
import pic2 from '../../../assets/images/forStudentsHome/pic2.png'
import pic2x2 from '../../../assets/images/forStudentsHome/pic2.png'
import StickyTitle from '../../common/stickyTitle'
import LinkModal from '../../common/linkModal'
import Helmet from '../../common/helmet'
import Pagination from '../../common/pagination'
import withParams from '../../common/withParams'
import GoalLink from './link'
// import Vote from './vote'
import styles from './indexStyles'

const GoalsList = ({goals, meta, params, setParams, classes}) => (
	<React.Fragment>
		{goals.map((goal) =>
			<GoalLink goal={goal} key={goal.id} />
		)}
		{meta && meta.total_pages > 1 ?
			<center><Pagination params={params} setParams={setParams} meta={meta}/></center> :
			<div className={classes.goalBox}>
				<Link to={goalNewPath()} className={classNames('btn', 'btn-success', classes.createButtonRound)}>
					<span className="ion-plus"/>
				</Link>
			</div>
		}
	</React.Fragment>
);

const NoGoals = ({classes, authorized, shared}) => (
	shared ?
		<center>At this moment you don&rsquo;t have shared plans.</center> :
		<React.Fragment>
			<div className={classes.section}>
				<div className={classNames(classes.sectionPicture)}>
					<img alt="Degree Plans" src={pic2} srcSet={`${pic2x2} 2x`} style={{maxWidth: '500px'}} />
				</div>
				<div className={classes.sectionText}>
					<h2>Personalized Degree Plans That Help Graduate Sooner</h2>
					<ul className={classes.list}>
						<li>
							Select your college
						</li>
						<li>
							Choose major / degree
						</li>
						<li>
							Calculate and see the optimal class completion plan toward your on-time graduation.
							We analyze your school’s class offerings, degree requirements, course prerequisites, etc*.
						</li>
					</ul>
					{authorized ? (
						<Link to={goalNewPath()} className={classNames('btn', 'btn-success', classes.createButton)}>
							<span className="ion-plus" /> Create New Study&nbsp;Plan
						</Link>
					) : (
						<LinkModal className={classNames('btn', 'btn-success', classes.createButton)} modal="SignIn">
							<span className="ion-plus" /> Sign in to create a Free Study&nbsp;Plan
						</LinkModal>
					)}
				</div>
			</div>
			{authorized ? (
				<Link to={goalNewPath()} className={classNames('btn', 'btn-success', classes.createButton, classes.createButtonBottom)}>
					<span className="ion-plus" /> Create New Study&nbsp;Plan
				</Link>
			) : (
				<LinkModal className={classNames('btn', 'btn-success', classes.createButton, classes.createButtonBottom)} modal="SignIn">
					<span className="ion-plus" /> Sign in to create a Free Study&nbsp;Plan
				</LinkModal>
			)}
			<div className={classes.sectionFineText}>
				* All personalized degree pathway calculations are based on your school’s course catalog data,
				department degree requirements, and general academic advising.
				<br />
				<span style={{visibility: 'hidden'}}>*</span> All degree plans generated using our services should
				solely be used as a guide. We strongly encourage students to share
				these generated degree pathways with their academic advisors.
			</div>
			{/* <Vote /> */}
		</React.Fragment>
);

const GoalsPresentation = ({classes, authorized, goals, meta, shared, params, setParams, newPath}) => (
	<div className={classes.container}>
		<Helmet title="Create a degree plan" titleTemplate={`%s ${params.page && parseInt(params.page) > 1 ? `- Page ${params.page} ` : ''}| Prepler`}/>
		<StickyTitle
			className={classes.bodyContainer}
			title={
				<React.Fragment>
					<div><span className={classNames({[classes.whenLarge]: goals.length > 0})}>{shared ? 'Shared' : 'My'} </span>Degree Plans</div>
				  {goals.length > 0 && (
						<Link to={goalNewPath()} className={classNames('btn', 'btn-success', classes.createButton)}>
							<span className="ion-plus" /> Create New<span className={classes.whenLarge}> Degree Plan</span>
						</Link>
					)}
				</React.Fragment>
			}
		>
			<div className={classes.body}>
				{authorized ? (
					<React.Fragment>
						{goals.length > 0 ? (
							<GoalsList
								goals={goals}
								meta={meta}
								params={params}
								setParams={setParams}
								classes={classes}
							/>
						) : (
							meta !== undefined && <NoGoals classes={classes} authorized={authorized} shared={shared}/>
						)}
					</React.Fragment>
				) : (
					<NoGoals classes={classes} authorized={authorized}/>
				)}
			</div>
		</StickyTitle>
	</div>
);

// eslint-disable-next-line react/no-deprecated
class Goals extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			showModal: true,
			loading: true
		}
	}

	openModalIfNeeded() {
		const {authorized, history: {push}, goals, meta, shared} = this.props;
		const {showModal, loading} = this.state;
		if (!loading && showModal && authorized && meta !== undefined && goals.length === 0 && !shared) {
			this.setState({showModal: false}, () => push(goalNewPath()));
		}
	}

	componentDidMount() {
		this.openModalIfNeeded();
		const {actions: {loadUniversitiesByIds, resetGoals}} = this.props;
		needLoadRunner(this.props, null, {universities: loadUniversitiesByIds});
		resetGoals();
		this.load();
	}

	componentDidUpdate(prevProps, prevState) {
		const {params, authorized, match: {path}, actions: {loadUniversitiesByIds}} = this.props;
		this.newPath = prevProps.match.path !== path || prevProps.authorized !== authorized;
		!this.newPath && this.openModalIfNeeded();
		needLoadRunner(this.props, prevProps, {universities: loadUniversitiesByIds});
		(!isEqual(prevProps.params, params) || this.newPath) && this.load();
	}

	load() {
		const {params, shared, authorized, actions: {loadGoals}} = this.props;
		if (!authorized) return;
		this.setState({loading: true});
		loadGoals({...params, shared}).then(() => this.setState({loading: false}));
	}

	render() {
		const {shared} = this.props;
		return <GoalsPresentation key={shared ? 'SharedGoals' : 'Goals'} {...this.props} newPath={this.newPath}/>;
	}
}

function mapStateToProps(state, ownProps) {
	// const {auth: {authorized}} = state;
	// We give ability unauthorized user to creat goals
	const shared = ownProps.match.path === routesHelper.sharedGoalsRegexp();
	const goals = goalsSelector(state);
	goals.goals.forEach((goal) => denormalizeUniversity(goal.university_id, state.entities, goals.needLoad));
	Object.keys(goals.needLoad).forEach((key) => goals.needLoad[key] = uniq(goals.needLoad[key]));
	return {
		authorized: true,
		...goals,
		shared
	};
}

function mapDispatchToProps(dispatch, ownProps) {
	return {
		actions: bindActionCreators(Object.assign({loadUniversitiesByIds}, goalActions), dispatch)
	};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(withParams(Goals))));
